if (!customElements.get('cart-drawer-recommendation-card')) {
  class CartDrawerRecommendationCard extends HTMLElement {
    connectedCallback() {
      this.variantSelect = this.querySelector('[data-rec-variant-select]');
      this.addButton = this.querySelector('[data-rec-add-button]');
      this.addButtonText = this.querySelector('[data-rec-add-button-text]');
      this.priceEl = this.querySelector('[data-rec-card-price]');
      this.priceTypeEl = this.querySelector('[data-rec-price-type]');
      this.sizeEl = this.querySelector('[data-rec-variant-size]');
      this.variants = this.#parseVariantData();
      this.variantSelect?.addEventListener('change', this.#onVariantChange);
      this.addButton?.addEventListener('click', this.#onAddClick);
      this.#updateUi();
    }

    disconnectedCallback() {
      this.variantSelect?.removeEventListener('change', this.#onVariantChange);
      this.addButton?.removeEventListener('click', this.#onAddClick);
    }

    #parseVariantData() {
      const script = this.querySelector('[data-rec-variant-data]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch {
        return [];
      }
    }

    #getSelectedVariantId() {
      if (this.variantSelect) {
        return Number(this.variantSelect.value);
      }
      return this.variants[0]?.id;
    }

    #getSelectedVariant() {
      const variantId = this.#getSelectedVariantId();
      return this.variants.find((variant) => variant.id === variantId);
    }

    #getLastSellingPlanId() {
      return Number(this.dataset.lastSellingPlanId) || 0;
    }

    #cartHasSubscription() {
      return this.dataset.cartHasSubscription === 'true';
    }

    #variantSupportsLastSellingPlan(variant) {
      if (!variant || !this.#getLastSellingPlanId()) return false;
      return variant.selling_plan_ids?.includes(this.#getLastSellingPlanId());
    }

    #shouldAddAsSubscription(variant = this.#getSelectedVariant()) {
      return this.#cartHasSubscription() && this.#variantSupportsLastSellingPlan(variant);
    }

    #formatMoney(cents) {
      return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
    }

    #getDisplayPriceCents(variant) {
      if (this.#shouldAddAsSubscription(variant)) {
        const planId = this.#getLastSellingPlanId();
        const plan = variant.selling_plans?.find((entry) => entry.id === planId);
        if (plan?.display_price != null) return plan.display_price;
      }

      if (variant.one_time_display_price != null) return variant.one_time_display_price;

      return variant.price;
    }

    #formatPrice(variant) {
      return this.#formatMoney(this.#getDisplayPriceCents(variant));
    }

    #updateUi() {
      const variant = this.#getSelectedVariant();
      if (!variant) return;

      if (this.sizeEl && !this.variantSelect) {
        this.sizeEl.textContent = variant.title === 'Default Title' ? '' : variant.title;
      }

      const addAsSubscription = this.#shouldAddAsSubscription(variant);

      if (this.priceEl) {
        this.priceEl.textContent = this.#formatPrice(variant);
      }

      if (this.priceTypeEl) {
        this.priceTypeEl.textContent = addAsSubscription
          ? this.dataset.labelOnSubscription
          : this.dataset.labelOneTime;
      }

      if (this.addButton) {
        this.addButton.disabled = !variant.available;
        this.addButton.classList.toggle('btn--primary', addAsSubscription);
        this.addButton.classList.toggle('btn--secondary', !addAsSubscription);
      }

      const label = addAsSubscription ? this.dataset.labelSubscription : this.dataset.labelCart;

      if (this.addButtonText) {
        this.addButtonText.textContent = label;
      }

      if (this.addButton && label) {
        this.addButton.setAttribute('aria-label', `${label}, ${this.#formatPrice(variant)}`);
      }
    }

    #onVariantChange = () => {
      this.#updateUi();
    };

    #onAddClick = async (event) => {
      event.preventDefault();

      const variant = this.#getSelectedVariant();
      if (!variant?.available || !this.addButton) return;

      this.addButton.setAttribute('aria-disabled', 'true');
      this.addButton.classList.add('btn--loading');

      const item = {
        id: variant.id,
        quantity: 1,
      };

      if (this.#shouldAddAsSubscription(variant)) {
        item.selling_plan = this.#getLastSellingPlanId();
      }

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      try {
        const response = await fetch(`${FoxTheme.routes.cart_add_url}`, {
          ...FoxTheme.utils.fetchConfig(),
          body: JSON.stringify({ items: [item], sections: sectionsToBundle }),
        });
        const parsedState = await response.json();

        if (parsedState.status) {
          throw new Error(parsedState.description || parsedState.message || 'Add to cart failed');
        }

        const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
        cartJson.sections = parsedState.sections;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          cart: cartJson,
          target: this,
        });

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cartJson } })
        );
      } catch (error) {
        console.error(error);
      } finally {
        this.addButton.classList.remove('btn--loading');
        this.addButton.removeAttribute('aria-disabled');
      }
    };
  }

  customElements.define('cart-drawer-recommendation-card', CartDrawerRecommendationCard);
}
